import { KeyboardEvent, useRef, useState } from 'react'
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import {
  Loading,
  mainColors,
  Modal,
  SNormal,
  Tooltip,
  XSNormal,
  zIndex,
} from '@avantstay/backoffice-core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconClose2,
  IconDownload,
  IconMinus,
  IconPlus,
} from '@avantstay/backoffice-vectors'

import { GuestInboxTimelineAttachment } from '@new/domains/operations/components/StayTimeline/StayTimeline.types'

import { FloatingFocusManager, useFloating } from '@floating-ui/react'

import { getAttachmentName } from '../../MessageAttachments.utils'
import * as S from './AttachmentViewer.styles'

const MIN_ZOOM = 1
const MAX_ZOOM = 4

interface AttachmentViewerProps {
  images: GuestInboxTimelineAttachment[]
  index: number
  getUrl: (image: GuestInboxTimelineAttachment) => string
  isDownloading: (image: GuestInboxTimelineAttachment) => boolean
  onDownload: (image: GuestInboxTimelineAttachment) => void
  onIndexChange: (index: number) => void
  onClose: () => void
}

export const AttachmentViewer = ({
  images,
  index,
  getUrl,
  isDownloading,
  onDownload,
  onIndexChange,
  onClose,
}: AttachmentViewerProps) => {
  const [brokenIds, setBrokenIds] = useState<string[]>([])
  const zoom = useRef<ReactZoomPanPinchRef>(null)
  const [scale, setScale] = useState(MIN_ZOOM)

  const { context, refs } = useFloating({ open: true })
  const returnFocus = useRef(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  )

  const current = images[index]
  const hasMany = images.length > 1
  const hasPrevious = index > 0
  const hasNext = index < images.length - 1
  const name = getAttachmentName(current)
  const src = getUrl(current)
  const isBroken = brokenIds.includes(current.id)

  const goTo = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= images.length) return

    setScale(MIN_ZOOM)
    onIndexChange(nextIndex)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
    } else if (event.key === 'ArrowLeft') {
      goTo(index - 1)
    } else if (event.key === 'ArrowRight') {
      goTo(index + 1)
    }
  }

  return (
    <Modal isClosable={false} zIndex={zIndex.FLOATING_MODAL}>
      <FloatingFocusManager
        restoreFocus
        context={context}
        initialFocus={refs.floating}
        returnFocus={returnFocus}
      >
        <S.Dialog
          ref={refs.setFloating}
          data-testid="attachment-viewer"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <S.Header>
            <S.Heading>
              <S.Title color={mainColors.primaryMedium}>{name}</S.Title>

              {hasMany ? (
                <XSNormal color={mainColors.neutralExtremelyDark}>
                  {index + 1} of {images.length}
                </XSNormal>
              ) : null}
            </S.Heading>

            <S.Actions>
              <Tooltip content="Download" placement="bottom">
                <S.HeaderButton
                  type="button"
                  data-testid="download-attachment"
                  onClick={() => onDownload(current)}
                >
                  {isDownloading(current) ? <Loading size="4px" /> : <IconDownload />}
                </S.HeaderButton>
              </Tooltip>

              <S.CloseButton type="button" onClick={onClose}>
                <IconClose2 />
              </S.CloseButton>
            </S.Actions>
          </S.Header>

          <S.Stage>
            {isBroken ? (
              <SNormal color={mainColors.neutralExtremelyDark}>
                This image could not be shown here. Download it to view it.
              </SNormal>
            ) : (
              <>
                <TransformWrapper
                  ref={zoom}
                  key={current.id}
                  minScale={MIN_ZOOM}
                  maxScale={MAX_ZOOM}
                  onTransformed={(_, state) => setScale(state.scale)}
                >
                  <TransformComponent>
                    <S.Image
                      src={src}
                      alt={name}
                      onError={() => setBrokenIds(ids => [...ids, current.id])}
                    />
                  </TransformComponent>
                </TransformWrapper>

                <S.ZoomControls>
                  <S.ZoomButton
                    type="button"
                    label="Zoom in"
                    data-disabled={scale >= MAX_ZOOM}
                    icon={<IconPlus />}
                    onClick={() => zoom.current?.zoomIn()}
                  />

                  <S.ZoomButton
                    type="button"
                    label="Zoom out"
                    data-disabled={scale <= MIN_ZOOM}
                    icon={<IconMinus />}
                    onClick={() => zoom.current?.zoomOut()}
                  />
                </S.ZoomControls>
              </>
            )}

            {hasMany ? (
              <>
                <S.PreviousButton
                  type="button"
                  label="Previous image"
                  data-disabled={!hasPrevious}
                  icon={<IconChevronLeft />}
                  onClick={() => goTo(index - 1)}
                />

                <S.NextButton
                  type="button"
                  label="Next image"
                  data-disabled={!hasNext}
                  icon={<IconChevronRight />}
                  onClick={() => goTo(index + 1)}
                />
              </>
            ) : null}
          </S.Stage>
        </S.Dialog>
      </FloatingFocusManager>
    </Modal>
  )
}
